import { useState, useEffect, useCallback } from "react";

export interface Participant {
  name: string;
  email: string;
  phone: string | null;
  note: string | null;
  availability: string[];
  reactions?: Record<string, string[]>;
  created_at: string;
}

export interface ResponseSummary {
  totalResponses: number;
  availabilityCounts: Record<string, number>;
  bestNight: { date: string; count: number } | null;
}

export interface UserResponse {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  note: string | null;
  availability: string[];
  reactions?: Record<string, string[]>;
  created_at: string;
  updated_at: string;
}

export function useAvailabilityData() {
  const [summary, setSummary] = useState<ResponseSummary | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [userResponse, setUserResponse] = useState<UserResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch("/api/responses");
      if (!res.ok) {
        throw new Error("Failed to fetch summary");
      }
      const data = await res.json();
      setSummary(data.summary);
      setParticipants(data.responses);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserResponse = useCallback(async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/responses?email=${encodeURIComponent(email)}`);
      if (!res.ok) {
        throw new Error("Failed to fetch user response");
      }
      const data = await res.json();
      setUserResponse(data.response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch user response");
    } finally {
      setLoading(false);
    }
  }, []);

  const saveResponse = useCallback(
    async (response: {
      name: string;
      email: string;
      phone?: string;
      note?: string;
      availability: string[];
    }) => {
      try {
        setLoading(true);
        setError(null);
        const isUpdate = userResponse !== null;
        const res = await fetch("/api/responses", {
          method: isUpdate ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(response),
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || "Failed to save response");
        }

        const data = await res.json();
        setUserResponse(data.response);
        await fetchSummary(); // Refresh summary
        return data.response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to save response";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [userResponse, fetchSummary]
  );

  const deleteResponse = useCallback(
    async (email: string) => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`/api/responses?email=${encodeURIComponent(email)}`, {
          method: "DELETE",
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || "Failed to delete response");
        }

        setUserResponse(null);
        // Force refresh summary by clearing it first, then fetching
        setSummary(null);
        await fetchSummary(); // Refresh summary
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to delete response";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchSummary]
  );

  const clearUserResponse = useCallback(() => {
    setUserResponse(null);
  }, []);

  const toggleReaction = useCallback(
    async (targetEmail: string, emoji: string, currentUserEmail: string) => {
      // Optimistically update local state
      const previousParticipants = [...participants];

      setParticipants((prev) =>
        prev.map((participant) => {
          if (participant.email !== targetEmail) {
            return participant;
          }

          const reactions = participant.reactions || {};
          const emojiReactions = reactions[emoji] || [];
          const hasReacted = emojiReactions.includes(currentUserEmail);

          const newReactions = { ...reactions };
          if (hasReacted) {
            // Remove reaction
            newReactions[emoji] = emojiReactions.filter((e) => e !== currentUserEmail);
            if (newReactions[emoji].length === 0) {
              delete newReactions[emoji];
            }
          } else {
            // Add reaction
            newReactions[emoji] = [...emojiReactions, currentUserEmail];
          }

          return {
            ...participant,
            reactions: newReactions,
          };
        })
      );

      try {
        const res = await fetch("/api/reactions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            targetEmail,
            emoji,
          }),
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || "Failed to toggle reaction");
        }

        const data = await res.json();

        // Update with server response
        setParticipants((prev) =>
          prev.map((participant) => {
            if (participant.email !== targetEmail) {
              return participant;
            }
            return {
              ...participant,
              reactions: data.reactions,
            };
          })
        );
      } catch (err) {
        // Rollback on error
        setParticipants(previousParticipants);
        const errorMessage = err instanceof Error ? err.message : "Failed to toggle reaction";
        setError(errorMessage);
        throw err;
      }
    },
    [participants]
  );

  // Helper function to check if user has reacted
  const hasReacted = useCallback(
    (participant: Participant, emoji: string, currentUserEmail: string): boolean => {
      const reactions = participant.reactions || {};
      const emojiReactions = reactions[emoji] || [];
      return emojiReactions.includes(currentUserEmail.toLowerCase().trim());
    },
    []
  );

  // Helper function to get reaction count
  const getReactionCount = useCallback((participant: Participant, emoji: string): number => {
    const reactions = participant.reactions || {};
    const emojiReactions = reactions[emoji] || [];
    return emojiReactions.length;
  }, []);

  return {
    summary,
    participants,
    userResponse,
    loading,
    error,
    fetchSummary,
    fetchUserResponse,
    saveResponse,
    deleteResponse,
    clearUserResponse,
    toggleReaction,
    hasReacted,
    getReactionCount,
  };
}

